import React, { useCallback, useEffect } from 'react';
import { omit } from 'ramda';
import PropTypes from 'prop-types';

import {
  ASC,
  DESIGNATION,
  DURATION,
  EYE,
  FREQUENCY,
  ID,
  LIMIT,
  QUANTITY,
  RETAIN_AFTER_EXAMINATION,
  SEARCH,
  SORT_BY,
  SORT_DIR,
  SUB_DRUG,
  TAGS,
  useAlerts,
  useMedicalHistoryActions,
  VALID_FROM,
  VALID_TO,
} from 'store/medicalHistory';
import PaginationDropDown from 'components/PaginationDropDown';
import Spinner from 'components/FullScreenSpinner';
import Message from 'components/Message';

import { usePatientMedications } from './hooks';
import DrugButton from './DrugButton';
import MedicationItem from './MedicationItem';
import { QuestionContainer, Container, Wrapper, Label } from './styles';

export default function Drugs({ readMode }) {
  const { fetchDrugs } = useMedicalHistoryActions();
  const { action, loading, success } = useAlerts(fetchDrugs);
  const getDrugs = useCallback(
    ($) =>
      fetchDrugs({
        ...omit([SEARCH], $),
        ...(($[SEARCH] || '').trim()
          ? { [SEARCH]: $[SEARCH] }
          : {
              [SORT_BY]: DESIGNATION,
              [SORT_DIR]: ASC,
            }),
      }),
    [fetchDrugs]
  );
  const { list, drugComparator, patientDrugList, addMedication, removeMedication, editLoading } = usePatientMedications();
  const onSelect = useCallback(
    (drug) => {
      if (!drugComparator[drug[ID]]) addMedication(drug);
    },
    [addMedication, drugComparator]
  );

  useEffect(() => {
    if (!readMode) action({ [TAGS]: 'favorite', [LIMIT]: 50 });
  }, [readMode, action]);

  if (!success || loading)
    return (
      <QuestionContainer label="Augen-Medikamente">
        <Spinner height="60px" />
      </QuestionContainer>
    );

  const medicationList = list?.length ? (
    list.map(
      ({
        [ID]: id,
        [SUB_DRUG]: drug,
        [QUANTITY]: quantity,
        [EYE]: eye,
        [FREQUENCY]: frequency,
        [DURATION]: duration,
        [VALID_FROM]: validFrom,
        [VALID_TO]: validTo,
        [RETAIN_AFTER_EXAMINATION]: retainAfterExamination,
      }) => (
        <MedicationItem
          key={id}
          id={id}
          drugName={drug?.[DESIGNATION]}
          quantity={quantity}
          eye={eye}
          frequency={frequency}
          duration={duration}
          validFrom={validFrom}
          validTo={validTo}
          retainAfterExamination={retainAfterExamination}
          onDelete={removeMedication}
          readMode={readMode}
          disabled={editLoading}
        />
      )
    )
  ) : (
    <Message textAlign="center">Es wurden noch keine Medikamente hinzugefügt</Message>
  );

  if (readMode) return <QuestionContainer label="Augen-Medikamente">{medicationList}</QuestionContainer>;

  return (
    <QuestionContainer label="Augen-Medikamente">
      {Boolean(success?.data?.length) && (
        <>
          <Label>Hinzufügen</Label>
          <Container>
            {success.data.map((item) => (
              <DrugButton
                key={item[ID]}
                data={item}
                onSelect={onSelect}
                isActive={Boolean(drugComparator[item[ID]])}
                disabled={editLoading}
              />
            ))}
          </Container>
        </>
      )}
      <PaginationDropDown
        idKey={ID}
        nameKey={DESIGNATION}
        action={getDrugs}
        placeholder="Suchen"
        value={patientDrugList}
        onChange={onSelect}
        disableIfSelected
        withoutSelectedList
        withoutRemoving
        onlyOne
      />
      <Label>Aktuelle Medikamente</Label>
      <Wrapper>{medicationList}</Wrapper>
    </QuestionContainer>
  );
}

Drugs.propTypes = { readMode: PropTypes.bool.isRequired };
